import { createReducer, createAction } from '@reduxjs/toolkit';

import { SensorManager, WebSocketManager, Loadable } from 'models';
import { makeThunkFromAPICall } from 'state';

export type SensorsState = {
    list: Loadable<SensorManager.Sensor[]>;
    fields: Loadable<SensorManager.SensorField[]>;
    records: Loadable<Record<string, SensorManager.SensorRecord[]>>;
    lastRefreshed: Record<string, number>;
};

const initialState: SensorsState = {
    list: { status: 'idle' },
    fields: { status: 'idle' },
    records: { status: 'idle' },
    lastRefreshed: {},
};

export const [getSensor, setSensor] = makeThunkFromAPICall(
    SensorManager.getSensorById,
    'Sensors/get',
);

export const [getSensorList, setSensorList] = makeThunkFromAPICall(
    SensorManager.listSensors,
    'Sensors/list',
);

export const [getSensorFieldList, setSensorFieldList] = makeThunkFromAPICall(
    SensorManager.listSensorFields,
    'Sensors/fields/list',
);

export const [getSensorRecords, setSensorRecords] = makeThunkFromAPICall(
    SensorManager.getRecords,
    'Sensors/records/list',
);

export const onAddSensorData =
    createAction<WebSocketManager.AddSensorData>('Sensor/records/add');

export const sensorsReducer = createReducer(initialState, (builder) => {
    builder.addCase(setSensorList, (state, action) => {
        return {
            ...state,
            list: { ...state.list, ...action.payload.loadable },
        };
    });

    builder.addCase(setSensorFieldList, (state, action) => {
        return {
            ...state,
            fields: { ...state.fields, ...action.payload.loadable },
        };
    });

    builder.addCase(setSensorRecords, (state, action) => {
        return {
            ...state,
            records: {
                ...state.records,
                status: action.payload.loadable.status,
                error: action.payload.loadable.error,
                object: action.payload.loadable.object?.reduce<
                    Record<string, SensorManager.SensorRecord[]>
                >(
                    (data, record) => {
                        data[record.field].push(record);
                        return data;
                    },
                    {
                        ...state.records.object,
                        [action.payload.input.field ?? '']: [],
                    },
                ),
            },
            lastRefreshed: {
                ...state.lastRefreshed,
                [action.payload.input.field ?? '']: Date.now(),
            },
        };
    });

    builder.addCase(onAddSensorData, (state, action) => {
        action.payload.records?.forEach((record) => {
            if (state.records.object && !state.records.object?.[record.field]) {
                state.records.object[record.field] = [];
            }
            state.records.object?.[record.field]?.push(record);
        });
    });
});

export const sensorsActions = {
    getSensorList,
    setSensorList,
    getSensorFieldList,
    setSensorFieldList,
    getSensorRecords,
    setSensorRecords,
    onAddSensorData,
    getSensor,
};

export default sensorsReducer;
